import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import QRCode from 'react-qr-code';
import QRCodeSecurity from '../../utils/qrCodeSecurity';
import { useAuth } from '../../contexts/AuthContext';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Alert,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import {
  ArrowBack,
  QrCode,
  Download,
  Print,
  Share,
  Person,
  GolfCourse,
  Refresh,
  Visibility,
  GetApp,
  Security,
  Warning,
  CheckCircle,
  Schedule
} from '@mui/icons-material';

export default function QRCodeManager() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentUser, userProfile } = useAuth();
  
  // State
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [qrCodeDialogOpen, setQrCodeDialogOpen] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [playerQRCodes, setPlayerQRCodes] = useState({});
  const [regenerating, setRegenerating] = useState({});

  useEffect(() => {
    fetchData();
  }, [tournamentId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch tournament
      const tournamentDoc = await getDoc(doc(db, 'tournaments', tournamentId));
      if (!tournamentDoc.exists()) {
        setMessage({ type: 'error', text: 'Tournament not found' });
        return;
      }
      const tournamentData = tournamentDoc.data();
      setTournament(tournamentData);

      // Fetch players for this tournament's organization
      const playersQuery = query(
        collection(db, 'players'),
        where('organizationId', '==', tournamentData.organizationId)
      );
      const playersSnapshot = await getDocs(playersQuery);
      const playersList = playersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPlayers(playersList);
      console.log('QRCodeManager - Fetched players:', playersList.length, 'for organization:', tournamentData.organizationId);

      // Load QR codes for all players
      await loadPlayerQRCodes(playersList, tournamentData);

    } catch (error) {
      console.error('Error fetching data:', error);
      setMessage({ type: 'error', text: 'Error loading tournament data' });
    } finally {
      setLoading(false);
    }
  };

  const loadPlayerQRCodes = async (playersList, tournamentData) => {
    try {
      console.log('Loading QR codes for players:', playersList.length, 'in tournament:', tournamentId);
      const qrCodes = {};
      
      for (const player of playersList) {
        const qrStatus = await QRCodeSecurity.getQRCodeStatus(tournamentId, player.id);
        console.log('QR status for player', player.id, ':', qrStatus);
        qrCodes[player.id] = qrStatus;
      }
      
      console.log('All QR codes loaded:', qrCodes);
      setPlayerQRCodes(qrCodes);
    } catch (error) {
      console.error('Error loading QR codes:', error);
    }
  };

  const generateQRCode = (player) => {
    if (!player || !player.id) return null;
    
    const qrData = playerQRCodes[player.id];
    if (qrData && qrData.exists && qrData.code) {
      return QRCodeSecurity.generateQRCodeURL(qrData.code);
    }
    return null;
  };

  const generateNewQRCode = async (player) => {
    if (!player || !player.id) return;
    
    try {
      console.log('Generating QR code for player:', player.id, 'in tournament:', tournamentId);
      setRegenerating(prev => ({ ...prev, [player.id]: true }));
      
      const generatedBy = currentUser?.email || userProfile?.email || 'Unknown';
      console.log('Generated by:', generatedBy);
      
      const newQRCode = await QRCodeSecurity.regenerateQRCode(tournamentId, player.id, generatedBy);
      console.log('Generated QR code:', newQRCode);
      
      // Update local state
      const qrStatus = await QRCodeSecurity.getQRCodeStatus(tournamentId, player.id);
      console.log('QR status after generation:', qrStatus);
      
      setPlayerQRCodes(prev => ({
        ...prev,
        [player.id]: qrStatus
      }));
      
      setMessage({ type: 'success', text: `New QR code generated for ${player.firstName} ${player.lastName}` });
      
    } catch (error) {
      console.error('Error generating QR code:', error);
      setMessage({ type: 'error', text: 'Error generating QR code: ' + error.message });
    } finally {
      setRegenerating(prev => ({ ...prev, [player.id]: false }));
    }
  };

  const handlePlayerClick = (player) => {
    setSelectedPlayer(player);
    setQrCodeDialogOpen(true);
  };

  const handleDownloadQR = (player) => {
    const canvas = document.getElementById(`qr-${player.id}`);
    if (canvas) {
      const link = document.createElement('a');
      link.download = `${player.firstName}_${player.lastName}_QR_Code.png`;
      link.href = canvas.toDataURL();
      link.click();
    }
  };

  const handlePrintQR = (player) => {
    const canvas = document.getElementById(`qr-${player.id}`);
    if (canvas) {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>QR Code - ${player.firstName} ${player.lastName}</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                text-align: center; 
                padding: 20px;
              }
              .qr-container { 
                margin: 20px 0; 
              }
              .player-info { 
                margin: 10px 0; 
                font-size: 18px;
              }
              .tournament-info { 
                margin: 10px 0; 
                color: #666;
              }
            </style>
          </head>
          <body>
            <h2>Mobile Score Entry QR Code</h2>
            <div class="player-info">
              <strong>${player.firstName} ${player.lastName}</strong>
            </div>
            <div class="tournament-info">
              ${tournament?.name || 'Tournament'}
            </div>
            <div class="qr-container">
              <img src="${canvas.toDataURL()}" alt="QR Code" style="max-width: 300px;" />
            </div>
            <p>Scan this QR code to access mobile score entry</p>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleShareQR = async (player) => {
    const qrUrl = generateQRCode(player);
    const shareText = `Mobile Score Entry QR Code for ${player.firstName} ${player.lastName} - ${tournament?.name}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Mobile Score Entry QR Code',
          text: shareText,
          url: qrUrl
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(qrUrl);
        setMessage({ type: 'success', text: 'QR Code URL copied to clipboard!' });
      } catch (error) {
        console.log('Error copying to clipboard:', error);
      }
    }
  };

  const getPlayerStatus = (player) => {
    const playerScores = tournament?.scores?.[player.id] || {};
    const holesCompleted = Object.keys(playerScores).length;
    
    if (holesCompleted === 0) return { status: 'not_started', color: 'default', text: 'Not Started' };
    if (holesCompleted === 18) return { status: 'completed', color: 'success', text: 'Completed' };
    return { status: 'in_progress', color: 'warning', text: `${holesCompleted}/18 Holes` };
  };

  const getQRCodeStatus = (player) => {
    if (!player || !player.id) {
      return { status: 'no_code', color: 'default', text: 'No Player', icon: <Warning /> };
    }
    
    const qrData = playerQRCodes[player.id];
    
    if (!qrData || !qrData.exists) {
      return { status: 'no_code', color: 'default', text: 'No QR Code', icon: <Warning /> };
    }
    
    if (qrData.isExpired) {
      return { status: 'expired', color: 'error', text: 'Expired', icon: <Warning /> };
    }
    
    if (!qrData.isActive) {
      return { status: 'inactive', color: 'default', text: 'Inactive', icon: <Warning /> };
    }
    
    const now = new Date();
    const expiresAt = new Date(qrData.expiresAt);
    const hoursUntilExpiry = Math.ceil((expiresAt - now) / (1000 * 60 * 60));
    
    if (hoursUntilExpiry <= 24) {
      return { status: 'expiring_soon', color: 'warning', text: `Expires in ${hoursUntilExpiry}h`, icon: <Schedule /> };
    }
    
    return { status: 'active', color: 'success', text: 'Active', icon: <CheckCircle /> };
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <Typography>Loading tournament data...</Typography>
      </Container>
    );
  }

  if (!tournament) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          Tournament not found. Please check your access.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <IconButton onClick={() => navigate(`/tournaments/${tournamentId}/director`)} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4">
            QR Codes - Mobile Score Entry
          </Typography>
        </Box>
        
        <Typography variant="body1" color="text.secondary">
          Generate QR codes for players to access mobile score entry. Each player gets a unique QR code 
          that takes them directly to their mobile scoring interface.
        </Typography>
      </Box>

      {/* Message */}
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      {/* Tournament Info */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <GolfCourse sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              {tournament.name}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {tournament.golfCourse?.name || 'Golf Course'} • {tournament.date}
          </Typography>
        </CardContent>
      </Card>

      {/* Players Grid */}
      <Grid container spacing={3}>
        {players && players.length > 0 ? players.map((player) => {
          if (!player) return null;
          const playerStatus = getPlayerStatus(player);
          const qrStatus = getQRCodeStatus(player);
          const qrCodeUrl = generateQRCode(player);
          
          return (
            <Grid item xs={12} sm={6} md={4} key={player.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 4
                  }
                }}
                onClick={() => handlePlayerClick(player)}
              >
                <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                  {/* QR Code Preview */}
                  <Box sx={{ mb: 2 }}>
                    {qrCodeUrl ? (
                      <QRCode 
                        value={qrCodeUrl} 
                        size={isMobile ? 120 : 150}
                        id={`qr-${player.id}`}
                      />
                    ) : (
                      <Box sx={{ 
                        width: isMobile ? 120 : 150, 
                        height: isMobile ? 120 : 150, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        bgcolor: 'grey.100',
                        borderRadius: 1
                      }}>
                        <Typography variant="body2" color="text.secondary">
                          No QR Code
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  
                  {/* Player Info */}
                  <Typography variant="h6" gutterBottom>
                    {player.firstName} {player.lastName}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {player.email || 'No email'}
                  </Typography>
                  
                  {/* Status Chips */}
                  <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Chip 
                      label={playerStatus.text}
                      color={playerStatus.color}
                      size="small"
                    />
                    <Chip 
                      label={qrStatus.text}
                      color={qrStatus.color}
                      size="small"
                      icon={qrStatus.icon}
                    />
                  </Box>
                  
                  {/* Handicap */}
                  {player.handicap && (
                    <Typography variant="body2" color="text.secondary">
                      Handicap: {player.handicap}
                    </Typography>
                  )}
                </CardContent>
                
                {/* Action Buttons */}
                <Box sx={{ p: 2, pt: 0 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={3}>
                      <Tooltip title="View QR Code">
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlayerClick(player);
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={3}>
                      <Tooltip title="Download QR Code">
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadQR(player);
                          }}
                          disabled={!qrCodeUrl}
                        >
                          <Download />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={3}>
                      <Tooltip title="Print QR Code">
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePrintQR(player);
                          }}
                          disabled={!qrCodeUrl}
                        >
                          <Print />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={3}>
                      <Tooltip title="Generate New QR Code">
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            generateNewQRCode(player);
                          }}
                          disabled={regenerating[player.id]}
                          color="primary"
                        >
                          {regenerating[player.id] ? <Refresh /> : <Security />}
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Box>
              </Card>
            </Grid>
          );
        }) : (
          <Grid item xs={12}>
            <Typography variant="body1" color="text.secondary" textAlign="center">
              Loading players...
            </Typography>
          </Grid>
        )}
      </Grid>

      {/* Empty State */}
      {players && players.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Person sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Players Found
          </Typography>
          <Typography color="text.secondary">
            Add players to this tournament to generate QR codes.
          </Typography>
        </Paper>
      )}

      {/* QR Code Dialog */}
      <Dialog 
        open={qrCodeDialogOpen} 
        onClose={() => setQrCodeDialogOpen(false)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <QrCode sx={{ mr: 1 }} />
            Mobile Score Entry QR Code
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {selectedPlayer && (
            <Box textAlign="center">
              {/* Large QR Code */}
              <Box sx={{ mb: 3 }}>
                {generateQRCode(selectedPlayer) ? (
                  <QRCode 
                    value={generateQRCode(selectedPlayer)} 
                    size={250}
                    id={`qr-dialog-${selectedPlayer.id}`}
                  />
                ) : (
                  <Box sx={{ 
                    width: 250, 
                    height: 250, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    bgcolor: 'grey.100',
                    borderRadius: 1
                  }}>
                    <Typography variant="h6" color="text.secondary">
                      No QR Code Available
                    </Typography>
                  </Box>
                )}
              </Box>
              
              {/* Player Info */}
              <Typography variant="h5" gutterBottom>
                {selectedPlayer.firstName} {selectedPlayer.lastName}
              </Typography>
              
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {tournament.name}
              </Typography>
              
              {/* QR Code Status */}
              {(() => {
                const qrStatus = getQRCodeStatus(selectedPlayer);
                const qrData = playerQRCodes[selectedPlayer.id];
                
                return (
                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label={qrStatus.text}
                      color={qrStatus.color}
                      icon={qrStatus.icon}
                      sx={{ mb: 1 }}
                    />
                    {qrData && qrData.exists && (
                      <Typography variant="body2" color="text.secondary">
                        Generated: {new Date(qrData.generatedAt).toLocaleString()}
                        <br />
                        Expires: {new Date(qrData.expiresAt).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                );
              })()}
              
              {/* URL */}
              {generateQRCode(selectedPlayer) && (
                <Paper sx={{ p: 2, bgcolor: 'grey.100', mb: 2 }}>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {generateQRCode(selectedPlayer)}
                  </Typography>
                </Paper>
              )}
              
              {/* Instructions */}
              <Alert severity="info" sx={{ textAlign: 'left' }}>
                <Typography variant="body2">
                  <strong>Instructions:</strong>
                  <br />• Player scans this QR code with their mobile device
                  <br />• Takes them directly to mobile score entry interface
                  <br />• QR code expires in 7 days for security
                  <br />• Generate new code if lost or expired
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setQrCodeDialogOpen(false)}>
            Close
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<Security />}
            onClick={() => selectedPlayer && generateNewQRCode(selectedPlayer)}
            disabled={regenerating[selectedPlayer?.id]}
          >
            {regenerating[selectedPlayer?.id] ? 'Generating...' : 'Generate New'}
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<Download />}
            onClick={() => selectedPlayer && handleDownloadQR(selectedPlayer)}
            disabled={!generateQRCode(selectedPlayer)}
          >
            Download
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<Print />}
            onClick={() => selectedPlayer && handlePrintQR(selectedPlayer)}
            disabled={!generateQRCode(selectedPlayer)}
          >
            Print
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Share />}
            onClick={() => selectedPlayer && handleShareQR(selectedPlayer)}
            disabled={!generateQRCode(selectedPlayer)}
          >
            Share
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
